import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AppSettings } from '../state/types';
import { DocumentModel } from '../core/text/types';
import { ScrollEngine } from '../core/scroll/engine';
import { ScrollEngineState } from '../core/scroll/types';
import { LayoutIndex } from '../core/geometry/layout_index';
import { VoiceController } from '../voice/voice_controller';
import { FocusArea } from './overlays/FocusArea';
import { FocusLines } from './overlays/FocusLines';
import { FocusMask } from './overlays/FocusMask';
import { ReadingMarker } from './overlays/ReadingMarker';
import { CountdownOverlay } from './overlays/CountdownOverlay';
import { ControlBar } from './ControlBar';
import { SettingsDrawer } from './SettingsDrawer';
import { DiagnosticsModal } from './DiagnosticsModal';
import { ModelManager } from '../voice/model_manager';

interface PrompterViewProps {
  doc: DocumentModel;
  settings: AppSettings;
  onUpdateSettings: (newSettings: Partial<AppSettings>) => void;
  onResetSettings: () => void;
  onBackToEditor: () => void;
  modelManager: ModelManager;
  voiceController: VoiceController;
}

export const PrompterView: React.FC<PrompterViewProps> = ({
  doc,
  settings,
  onUpdateSettings,
  onResetSettings,
  onBackToEditor,
  modelManager,
  voiceController,
}) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const contentLayerRef = useRef<HTMLDivElement>(null);
  const scrollEngineRef = useRef<ScrollEngine | null>(null);
  const layoutIndexRef = useRef<LayoutIndex>(new LayoutIndex());
  const wakeLockRef = useRef<any>(null);

  const [scrollState, setScrollState] = useState<ScrollEngineState>(() => ({
    y: 0,
    maxY: 0,
    targetSpeed: settings.speed,
    currentSpeed: 0,
    playState: 'paused',
    controlSource: settings.modePreference,
    clampedFrameCount: 0,
  }));

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDiagnosticsOpen, setIsDiagnosticsOpen] = useState(false);
  const [countdownRemaining, setCountdownRemaining] = useState(settings.countdownSeconds);
  const [voiceState, setVoiceState] = useState(() => voiceController.getState());

  const hideControlsTimerRef = useRef<any>(null);
  const countdownTimerRef = useRef<any>(null);
  const lastRenderedYRef = useRef<number>(-1);

  // Focus geometry
  const focusGeometry = {
    centerPercent: settings.focusAreaPosition,
    heightPercent: settings.focusAreaHeight,
  };

  // Roll padding state
  const [rollPadding, setRollPadding] = useState({ preRoll: 300, postRoll: 500 });

  // 1. Initialize Engine & Voice Subsystem
  useEffect(() => {
    const engine = new ScrollEngine();
    engine.setSpeed(settings.speed);
    engine.setControlSource(settings.modePreference);
    scrollEngineRef.current = engine;

    voiceController.attach(engine, layoutIndexRef.current);
    voiceController.setTokens(doc.tokens);

    const unsubEngine = engine.onChange((st) => {
      setScrollState(st);
    });

    voiceController.onStateChange((vs) => {
      setVoiceState(vs);
    });

    return () => {
      unsubEngine();
      engine.pause();
      voiceController.stop();
      voiceController.detach();
    };
  }, []);

  // 2. Measure DOM Layout & Re-anchor on Resize or Font change
  const measureLayout = useCallback(() => {
    const contentEl = contentLayerRef.current;
    const stageEl = stageRef.current;
    if (!contentEl || !stageEl || !scrollEngineRef.current) return;

    const viewportH = stageEl.clientHeight || window.innerHeight;
    const padding = LayoutIndex.calculateRollPadding(viewportH, settings.focusAreaPosition);
    setRollPadding(padding);

    // Anchor preservation (§7)
    const anchor = layoutIndexRef.current.captureAnchor(
      scrollEngineRef.current.getState().y,
      viewportH,
      focusGeometry
    );

    // Measure untransformed layout
    layoutIndexRef.current.measure(contentEl, doc);
    scrollEngineRef.current.setContent(contentEl.scrollHeight, viewportH);

    // Restore anchor
    if (anchor) {
      const restoredY = layoutIndexRef.current.restoreAnchor(anchor, viewportH, focusGeometry);
      if (restoredY !== null) {
        scrollEngineRef.current.seek(restoredY);
      }
    }
  }, [doc, settings.focusAreaPosition, settings.fontSize, settings.fontFamily, settings.lineHeight]);

  // Measure on mount, document change, or font load
  useEffect(() => {
    voiceController.setTokens(doc.tokens);
    measureLayout();
    if (document.fonts?.ready) {
      document.fonts.ready.then(measureLayout);
    }
  }, [measureLayout, doc.tokens, voiceController]);

  // ResizeObserver for viewport changes
  useEffect(() => {
    const stageEl = stageRef.current;
    if (!stageEl) return;

    const observer = new ResizeObserver(() => {
      measureLayout();
    });
    observer.observe(stageEl);
    return () => observer.disconnect();
  }, [measureLayout]);

  // 3. Single rAF Animation Loop (§6 & INV-4, INV-5)
  useEffect(() => {
    let animId: number;
    let lastTime = performance.now();

    const loop = (now: number) => {
      const dtMs = now - lastTime;
      lastTime = now;
      const dtSec = Math.max(0, Math.min(0.1, dtMs / 1000.0));

      const engine = scrollEngineRef.current;
      const stageEl = stageRef.current;

      if (engine && stageEl) {
        const viewportH = stageEl.clientHeight || window.innerHeight;

        // If in voice mode, tick follow controller
        if (engine.getState().controlSource === 'voice') {
          voiceController.tickFollow(viewportH, focusGeometry, dtSec, now);
        }

        // Advance kinematic engine
        const st = engine.tick(now);

        // Render DOM transform ONLY if y changed (INV-5: no per-frame layout reads)
        const contentEl = contentLayerRef.current;
        if (contentEl && Math.abs(st.y - lastRenderedYRef.current) > 0.01) {
          lastRenderedYRef.current = st.y;
          contentEl.style.transform = `translate3d(0, ${-st.y}px, 0)`;
        }
      }

      animId = requestAnimationFrame(loop);
    };

    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, [settings.focusAreaPosition, settings.focusAreaHeight]);

  // 4. Wake Lock Management (§9)
  useEffect(() => {
    const requestWakeLock = async () => {
      if ('wakeLock' in navigator && !wakeLockRef.current) {
        try {
          wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
        } catch {
          // ignore
        }
      }
    };

    requestWakeLock();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
        scrollEngineRef.current?.resetClock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  // 5. Autohide Controls Logic (§9)
  const resetAutohide = useCallback(() => {
    setControlsVisible(true);
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
    }

    if (scrollEngineRef.current?.getState().playState === 'playing') {
      hideControlsTimerRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
    }
  }, []);

  useEffect(() => {
    resetAutohide();
    return () => {
      if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    };
  }, [scrollState.playState, resetAutohide]);

  // 6. Countdown Sequence (§6)
  useEffect(() => {
    if (countdownRemaining > 0) {
      countdownTimerRef.current = setInterval(() => {
        setCountdownRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimerRef.current);
            // Start scrolling
            if (settings.modePreference === 'voice') {
              scrollEngineRef.current?.setControlSource('voice');
              voiceController.start(settings.micDeviceId).catch((err) => {
                console.warn('[PrompterView] Voice follow error, falling back to manual:', err);
                scrollEngineRef.current?.setControlSource('manual');
                scrollEngineRef.current?.play();
              });
            } else {
              scrollEngineRef.current?.setControlSource('manual');
              scrollEngineRef.current?.play();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownTimerRef.current);
    } else {
      // If countdown is 0 initially, start immediately
      if (settings.modePreference === 'voice') {
        scrollEngineRef.current?.setControlSource('voice');
        voiceController.start(settings.micDeviceId).catch((err) => {
          console.warn('[PrompterView] Voice follow error, falling back to manual:', err);
          scrollEngineRef.current?.setControlSource('manual');
          scrollEngineRef.current?.play();
        });
      } else {
        scrollEngineRef.current?.setControlSource('manual');
        scrollEngineRef.current?.play();
      }
    }
  }, []);

  // 7. Input Handling: Wheel, Touch, Keyboard
  const handleWheel = (e: React.WheelEvent) => {
    resetAutohide();
    if (!scrollEngineRef.current) return;

    let deltaY = e.deltaY;
    if (e.deltaMode === 1) deltaY *= 32; // Line mode
    if (e.deltaMode === 2) deltaY *= 800; // Page mode

    // Invert delta under vertical mirror (§8)
    if (settings.mirrorMode === 'vertical' || settings.mirrorMode === 'both') {
      deltaY = -deltaY;
    }

    scrollEngineRef.current.applyUserDelta(deltaY);

    if (scrollEngineRef.current.getState().controlSource === 'voice') {
      const stageEl = stageRef.current;
      const vh = stageEl?.clientHeight || window.innerHeight;
      voiceController.notifyManualOverride(vh, focusGeometry);
    }
  };

  // Keyboard Shortcuts (§10)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable ||
        e.isComposing
      ) {
        return;
      }

      resetAutohide();

      const engine = scrollEngineRef.current;
      if (!engine) return;

      const vh = stageRef.current?.clientHeight || window.innerHeight;

      // Space: Play/Pause or Cancel Countdown
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();

        // If countdown active, cancel countdown and stay stopped
        if (countdownRemaining > 0) {
          clearInterval(countdownTimerRef.current);
          setCountdownRemaining(0);
          engine.pause();
          return;
        }

        if (engine.getState().controlSource === 'voice') {
          voiceController.toggleHold();
        } else {
          if (engine.getState().playState === 'playing') {
            engine.pause();
          } else {
            engine.play();
          }
        }
        return;
      }

      // Up / Down: Speed Adjustment
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const factor = e.shiftKey ? 1.15 : 1.04;
        const newSpeed = Math.min(1500, Math.round(settings.speed * factor));
        engine.setSpeed(newSpeed);
        onUpdateSettings({ speed: newSpeed });
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const factor = e.shiftKey ? 0.85 : 0.96;
        const newSpeed = Math.max(2, Math.round(settings.speed * factor));
        engine.setSpeed(newSpeed);
        onUpdateSettings({ speed: newSpeed });
        return;
      }

      // PageUp / PageDown: Reposition ~80% viewport
      if (e.key === 'PageUp') {
        e.preventDefault();
        const dy = -(vh * 0.8);
        engine.applyUserDelta(dy);
        if (engine.getState().controlSource === 'voice') {
          voiceController.notifyManualOverride(vh, focusGeometry);
        }
        return;
      }
      if (e.key === 'PageDown') {
        e.preventDefault();
        const dy = vh * 0.8;
        engine.applyUserDelta(dy);
        if (engine.getState().controlSource === 'voice') {
          voiceController.notifyManualOverride(vh, focusGeometry);
        }
        return;
      }

      // Home / End: Jump to Start / End
      if (e.key === 'Home') {
        e.preventDefault();
        engine.seek(0);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        engine.seek(engine.getState().maxY);
        return;
      }

      // F: Fullscreen Toggle
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
        return;
      }

      // V: Toggle Voice Follow Mode
      if (e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        toggleVoiceMode();
        return;
      }

      // Esc: Close settings / modals
      if (e.key === 'Escape') {
        if (isSettingsOpen) {
          setIsSettingsOpen(false);
          return;
        }
        if (isDiagnosticsOpen) {
          setIsDiagnosticsOpen(false);
          return;
        }
      }

      // Ctrl / Cmd + / - / 0 Font Controls
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd') {
          e.preventDefault();
          changeFontSize(4);
        } else if (e.key === '-' || e.code === 'NumpadSubtract') {
          e.preventDefault();
          changeFontSize(-4);
        } else if (e.key === '0' || e.code === 'Numpad0') {
          e.preventDefault();
          onUpdateSettings({ fontSize: 64 });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [countdownRemaining, settings.speed, settings.fontSize, isSettingsOpen, isDiagnosticsOpen]);

  const changeFontSize = (delta: number) => {
    const oldFont = settings.fontSize;
    const newFont = Math.max(24, Math.min(400, oldFont + delta));
    if (newFont !== oldFont) {
      if (settings.keepPaceOnFontChange) {
        const speedRatio = newFont / oldFont;
        const newSpeed = Math.max(2, Math.min(1500, Math.round(settings.speed * speedRatio)));
        scrollEngineRef.current?.setSpeed(newSpeed);
        onUpdateSettings({ fontSize: newFont, speed: newSpeed });
      } else {
        onUpdateSettings({ fontSize: newFont });
      }
    }
  };

  const toggleVoiceMode = () => {
    const engine = scrollEngineRef.current;
    if (!engine) return;

    if (engine.getState().controlSource === 'manual') {
      engine.setControlSource('voice');
      voiceController.start(settings.micDeviceId).catch(console.warn);
      onUpdateSettings({ modePreference: 'voice' });
    } else {
      voiceController.stop();
      engine.setControlSource('manual');
      onUpdateSettings({ modePreference: 'manual' });
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      stageRef.current?.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  // Mirror transform calculation for the STAGE wrapper only (§8)
  let mirrorTransform = 'none';
  if (settings.mirrorMode === 'horizontal') mirrorTransform = 'scaleX(-1)';
  else if (settings.mirrorMode === 'vertical') mirrorTransform = 'scaleY(-1)';
  else if (settings.mirrorMode === 'both') mirrorTransform = 'scale(-1, -1)';

  // Font class & text color preset
  const fontClass =
    settings.fontFamily === 'naskh'
      ? 'font-naskh'
      : settings.fontFamily === 'amiri'
      ? 'font-amiri'
      : 'font-sans';

  const textColor =
    settings.textColorPreset === 'warm'
      ? '#f5f5dc'
      : settings.textColorPreset === 'yellow'
      ? '#ffff00'
      : '#ffffff';

  // Column width class
  const colWidthClass =
    settings.textColumnWidth === 'narrow'
      ? 'max-w-3xl'
      : settings.textColumnWidth === 'normal'
      ? 'max-w-5xl'
      : 'max-w-7xl';

  return (
    <div
      ref={stageRef}
      onWheel={handleWheel}
      onMouseMove={resetAutohide}
      onTouchStart={resetAutohide}
      className="relative w-full h-full bg-black overflow-hidden select-none cursor-default"
      style={{ cursor: controlsVisible ? 'default' : 'none' }}
    >
      {/* 
        STAGE MIRROR WRAPPER:
        Applies CSS transform to content and overlays, leaving ControlBar unmirrored (§8)
      */}
      <div
        className="w-full h-full relative overflow-hidden"
        style={{ transform: mirrorTransform }}
      >
        {/* Presentation Overlays (§8) */}
        <FocusArea
          enabled={settings.focusAreaEnabled}
          centerPercent={settings.focusAreaPosition}
          heightPercent={settings.focusAreaHeight}
          intensity={settings.focusAreaIntensity}
        />

        <FocusLines
          enabled={settings.focusLinesEnabled}
          centerPercent={settings.focusAreaPosition}
          heightPercent={settings.focusAreaHeight}
          thickness={settings.focusLinesThickness}
          opacity={settings.focusLinesOpacity}
        />

        <FocusMask
          enabled={settings.focusMaskEnabled}
          centerPercent={settings.focusAreaPosition}
          heightPercent={settings.focusAreaHeight}
          opacity={settings.focusMaskOpacity}
        />

        <ReadingMarker
          enabled={settings.markerEnabled}
          centerPercent={settings.focusAreaPosition}
          heightPercent={settings.focusAreaHeight}
          color={settings.markerColor}
          width={settings.markerWidth}
          offset={settings.markerOffset}
          opacity={settings.markerOpacity}
          side={settings.markerSide}
          isRtl={true}
        />

        <CountdownOverlay remainingSeconds={countdownRemaining} />

        {/* 
          HARDWARE-ACCELERATED CONTENT LAYER:
          Written via direct transform translate3d(0, -y, 0) inside rAF (INV-4, INV-5)
        */}
        <div
          ref={contentLayerRef}
          className="w-full absolute top-0 left-0 will-change-transform pointer-events-none"
          style={{
            paddingTop: `${rollPadding.preRoll}px`,
            paddingBottom: `${rollPadding.postRoll}px`,
          }}
        >
          <div className={`mx-auto px-6 sm:px-12 ${colWidthClass}`}>
            {doc.blocks.map((block) => (
              <p
                key={block.index}
                data-block-index={block.index}
                dir="auto"
                className={`arabic-script-surface mb-6 ${fontClass} ${
                  settings.textAlign === 'center' ? 'text-center' : 'text-start'
                }`}
                style={{
                  fontSize: `${settings.fontSize}px`,
                  lineHeight: settings.lineHeight,
                  color: textColor,
                  unicodeBidi: 'plaintext',
                  overflowWrap: 'break-word',
                  minHeight: block.isBlank ? `${settings.fontSize * settings.lineHeight}px` : undefined,
                }}
              >
                {block.text || '\u00A0'}
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Operator Control Bar (UNMIRRORED, §8) */}
      <ControlBar
        playState={scrollState.playState}
        controlSource={scrollState.controlSource}
        isVoiceActive={voiceState.isMicActive}
        isVoiceHold={voiceState.isHold}
        speed={scrollState.targetSpeed}
        fontSize={settings.fontSize}
        isFullscreen={isFullscreen}
        progressFraction={scrollState.maxY > 0 ? scrollState.y / scrollState.maxY : 0}
        visible={controlsVisible}
        onPlayPause={() => {
          if (scrollState.controlSource === 'voice') {
            voiceController.toggleHold();
          } else {
            if (scrollState.playState === 'playing') {
              scrollEngineRef.current?.pause();
            } else {
              scrollEngineRef.current?.play();
            }
          }
        }}
        onReset={() => scrollEngineRef.current?.reset()}
        onSpeedDelta={(deltaPercent) => {
          const factor = 1 + deltaPercent / 100.0;
          const newSpeed = Math.max(2, Math.min(1500, Math.round(settings.speed * factor)));
          scrollEngineRef.current?.setSpeed(newSpeed);
          onUpdateSettings({ speed: newSpeed });
        }}
        onFontDelta={changeFontSize}
        onToggleVoice={toggleVoiceMode}
        onToggleFullscreen={toggleFullscreen}
        onOpenSettings={() => setIsSettingsOpen(true)}
        onBackToEditor={() => {
          scrollEngineRef.current?.pause();
          voiceController.stop();
          onBackToEditor();
        }}
      />

      {/* Settings Drawer */}
      <SettingsDrawer
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        onUpdateSettings={onUpdateSettings}
        onResetSettings={onResetSettings}
        modelManager={modelManager}
        onOpenDiagnostics={() => setIsDiagnosticsOpen(true)}
      />

      {/* Diagnostics Modal */}
      <DiagnosticsModal
        isOpen={isDiagnosticsOpen}
        onClose={() => setIsDiagnosticsOpen(false)}
        voiceState={voiceState}
        scrollState={scrollState}
      />
    </div>
  );
};
